import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Plus,
  Pencil,
  Trash2,
  AlertCircle,
  Clock,
  Target,
  RefreshCw,
  CheckSquare,
  MessageSquare,
  Rocket,
  Flag,
  Play,
  Presentation,
  X,
  Loader2,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { PREDEFINED_DATE_TYPES, DATE_TYPE_LIST } from '@yoda/shared';
import type { WorkItemDate, DateType } from '@yoda/shared';
import { api } from '../../lib/api';
import toast from 'react-hot-toast';

interface DateManagerProps {
  workItemId: string;
  dates: WorkItemDate[];
  onDatesChange: (dates: WorkItemDate[]) => void;
}

const dateTypeIcons: Record<string, typeof Calendar> = {
  deadline: AlertCircle,
  due_date: Calendar,
  next_update: RefreshCw,
  expected_completion: Target,
  estimated_completion: Clock,
  review_date: CheckSquare,
  feedback_due: MessageSquare,
  go_live_date: Rocket,
  milestone_date: Flag,
  start_date: Play,
  presentation_date: Presentation,
};

export function DateManager({ workItemId, dates, onDatesChange }: DateManagerProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDate, setEditingDate] = useState<WorkItemDate | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dateTypes, setDateTypes] = useState<DateType[]>([]);

  // Form state
  const [useCustomLabel, setUseCustomLabel] = useState(false);
  const [selectedDateTypeId, setSelectedDateTypeId] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [dateValue, setDateValue] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchDateTypes();
  }, []);

  const fetchDateTypes = async () => {
    try {
      const res = await api.get<DateType[]>('/work-items/date-types');
      setDateTypes(res.data);
    } catch (err) {
      console.error('Failed to fetch date types:', err);
    }
  };

  const openAddModal = () => {
    setEditingDate(null);
    setUseCustomLabel(false);
    setSelectedDateTypeId('');
    setCustomLabel('');
    setDateValue('');
    setNotes('');
    setIsModalOpen(true);
  };

  const openEditModal = (date: WorkItemDate) => {
    setEditingDate(date);
    setUseCustomLabel(!!date.customLabel);
    setSelectedDateTypeId(date.dateTypeId || '');
    setCustomLabel(date.customLabel || '');
    setDateValue(date.dateValue.split('T')[0]);
    setNotes(date.notes || '');
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingDate(null);
  };

  const handleSubmit = async () => {
    if (!dateValue) {
      toast.error('Please select a date');
      return;
    }

    if (!useCustomLabel && !selectedDateTypeId) {
      toast.error('Please select a date type');
      return;
    }

    if (useCustomLabel && !customLabel.trim()) {
      toast.error('Please enter a custom label');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        dateTypeId: useCustomLabel ? undefined : selectedDateTypeId,
        customLabel: useCustomLabel ? customLabel.trim() : undefined,
        dateValue,
        notes: notes.trim() || undefined,
      };

      if (editingDate) {
        const res = await api.patch<WorkItemDate>(
          `/work-items/${workItemId}/dates/${editingDate.id}`,
          payload
        );
        onDatesChange(dates.map((d) => (d.id === editingDate.id ? res.data : d)));
        toast.success('Date updated');
      } else {
        const res = await api.post<WorkItemDate>(`/work-items/${workItemId}/dates`, payload);
        onDatesChange([...dates, res.data]);
        toast.success('Date added');
      }
      closeModal();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save date');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (dateId: string) => {
    if (!confirm('Delete this date?')) return;

    try {
      await api.delete(`/work-items/${workItemId}/dates/${dateId}`);
      onDatesChange(dates.filter((d) => d.id !== dateId));
      toast.success('Date deleted');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete date');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getDateColor = (date: WorkItemDate) => {
    if (date.dateTypeId && PREDEFINED_DATE_TYPES[date.dateTypeId as keyof typeof PREDEFINED_DATE_TYPES]) {
      return PREDEFINED_DATE_TYPES[date.dateTypeId as keyof typeof PREDEFINED_DATE_TYPES].color;
    }
    return '#6366f1';
  };

  const getDateIcon = (date: WorkItemDate) => {
    if (date.dateTypeId && dateTypeIcons[date.dateTypeId]) {
      return dateTypeIcons[date.dateTypeId];
    }
    return Calendar;
  };

  const isOverdue = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <Card hover={false}>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Key Dates
        </CardTitle>
        <Button size="sm" variant="secondary" onClick={openAddModal}>
          <Plus className="w-4 h-4" />
          Add Date
        </Button>
      </CardHeader>
      <CardContent>
        {dates.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-4">No dates set</p>
        ) : (
          <div className="space-y-2">
            <AnimatePresence mode="popLayout">
              {dates.map((date) => {
                const Icon = getDateIcon(date);
                const color = getDateColor(date);
                const overdue = isOverdue(date.dateValue);

                return (
                  <motion.div
                    key={date.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="group flex items-center justify-between p-3 rounded-lg bg-bg-tertiary hover:bg-bg-secondary transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color }} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{date.label}</p>
                        <p
                          className={`text-sm ${
                            overdue ? 'text-red-500 font-medium' : 'text-text-muted'
                          }`}
                        >
                          {formatDate(date.dateValue)}
                          {overdue && ' (Overdue)'}
                        </p>
                        {date.notes && (
                          <p className="text-xs text-text-muted mt-1">{date.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => openEditModal(date)}
                        className="p-2 rounded-lg hover:bg-bg-tertiary transition-colors"
                      >
                        <Pencil className="w-4 h-4 text-text-muted" />
                      </button>
                      <button
                        onClick={() => handleDelete(date.id)}
                        className="p-2 rounded-lg hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </CardContent>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingDate ? 'Edit Date' : 'Add Date'}
      >
        <div className="space-y-4">
          {/* Date Type Toggle */}
          <div className="flex gap-2 p-1 rounded-xl bg-bg-tertiary">
            <button
              onClick={() => setUseCustomLabel(false)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                !useCustomLabel
                  ? 'bg-accent text-white'
                  : 'text-text-secondary hover:text-text'
              }`}
            >
              Predefined Type
            </button>
            <button
              onClick={() => setUseCustomLabel(true)}
              className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                useCustomLabel
                  ? 'bg-accent text-white'
                  : 'text-text-secondary hover:text-text'
              }`}
            >
              Custom Label
            </button>
          </div>

          {/* Date Type Selection */}
          {!useCustomLabel ? (
            <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
              {DATE_TYPE_LIST.map((type) => {
                const Icon = dateTypeIcons[type.id] || Calendar;
                const isSelected = selectedDateTypeId === type.id;

                return (
                  <button
                    key={type.id}
                    onClick={() => setSelectedDateTypeId(type.id)}
                    className={`flex items-center gap-2 p-3 rounded-xl border text-left transition-colors ${
                      isSelected
                        ? 'border-accent bg-accent/10'
                        : 'border-border hover:border-accent/50'
                    }`}
                  >
                    <Icon className="w-4 h-4" style={{ color: type.color }} />
                    <span className="text-sm font-medium">{type.label}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <Input
              label="Custom Label"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="e.g., Client Review"
            />
          )}

          {/* Date Picker */}
          <Input
            label="Date"
            type="date"
            value={dateValue}
            onChange={(e) => setDateValue(e.target.value)}
            required
          />

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-2">Notes (Optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full px-4 py-3 rounded-xl bg-bg-tertiary border border-border focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20 resize-none text-sm"
              placeholder="Add any additional notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} isLoading={isSubmitting}>
              {editingDate ? 'Update' : 'Add Date'}
            </Button>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
